import { ReactNode, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragOverEvent,
  type DragStartEvent,
} from "@dnd-kit/core";
import { SortableContext, useSortable } from "@dnd-kit/sortable";
import { reorderArray } from "../lib/reorderArray";

export interface SortableGridItem {
  id: string;
}

type SortableHookResult = ReturnType<typeof useSortable>;

export interface DragHandleProps {
  attributes: SortableHookResult["attributes"];
  listeners: SortableHookResult["listeners"];
  setActivatorNodeRef: SortableHookResult["setActivatorNodeRef"];
}

interface SortableFavoriteSearchGridProps<T extends SortableGridItem> {
  items: T[];
  layout: "grid" | "list";
  disabled?: boolean;
  onReorder: (orderedIds: string[]) => void;
  renderItem: (item: T, dragHandleProps: DragHandleProps | null) => ReactNode;
  className?: string;
}

interface SortableItemRowProps<T extends SortableGridItem> {
  item: T;
  disabled?: boolean;
  renderItem: (item: T, dragHandleProps: DragHandleProps | null) => ReactNode;
}

const noSortingStrategy = () => null;

const SortableItemRow = <T extends SortableGridItem>({
  item,
  disabled = false,
  renderItem,
}: SortableItemRowProps<T>) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    isDragging,
  } = useSortable({ id: item.id, disabled });

  const style = {
    opacity: isDragging ? 0 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} className="relative min-w-0">
      {renderItem(
        item,
        disabled ? null : { attributes, listeners, setActivatorNodeRef }
      )}
    </div>
  );
};

function SortableFavoriteSearchGrid<T extends SortableGridItem>({
  items,
  layout: _layout,
  disabled = false,
  onReorder,
  renderItem,
  className,
}: SortableFavoriteSearchGridProps<T>) {
  const [activeId, setActiveId] = useState<string | null>(null);
  const [dragItems, setDragItems] = useState(items);
  const [overlayWidth, setOverlayWidth] = useState<number | undefined>(
    undefined
  );

  useEffect(() => {
    if (activeId !== null) return;
    setDragItems(items);
  }, [items, activeId]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
    })
  );

  const itemIds = dragItems.map((item) => item.id);
  const activeItem = dragItems.find((item) => item.id === activeId) ?? null;

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(String(event.active.id));
    setOverlayWidth(event.active.rect.current?.initial?.width ?? undefined);
  };

  const handleDragOver = (event: DragOverEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    setDragItems((current) => {
      const oldIndex = current.findIndex((item) => item.id === active.id);
      const newIndex = current.findIndex((item) => item.id === over.id);
      if (oldIndex === -1 || newIndex === -1 || oldIndex === newIndex) {
        return current;
      }

      return reorderArray(current, oldIndex, newIndex);
    });
  };

  const handleDragEnd = (_event: DragEndEvent) => {
    setActiveId(null);
    setOverlayWidth(undefined);

    setDragItems((current) => {
      const newIds = current.map((item) => item.id);
      const oldIds = items.map((item) => item.id);
      if (newIds.some((id, index) => id !== oldIds[index])) {
        onReorder(newIds);
      }
      return current;
    });
  };

  const handleDragCancel = () => {
    setActiveId(null);
    setOverlayWidth(undefined);
    setDragItems(items);
  };

  if (disabled) {
    return (
      <div className={className}>
        {items.map((item) => (
          <div key={item.id} className="relative min-w-0">
            {renderItem(item, null)}
          </div>
        ))}
      </div>
    );
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      <SortableContext items={itemIds} strategy={noSortingStrategy}>
        <div className={className}>
          {dragItems.map((item) => (
            <SortableItemRow
              key={item.id}
              item={item}
              renderItem={renderItem}
            />
          ))}
        </div>
      </SortableContext>
      {typeof document !== "undefined"
        ? createPortal(
            <DragOverlay
              dropAnimation={{ duration: 200, easing: "ease" }}
              zIndex={1000}
            >
              {activeItem ? (
                <div
                  style={{ width: overlayWidth }}
                  className="relative min-w-0 cursor-grabbing shadow-lg opacity-95"
                >
                  {renderItem(activeItem, null)}
                </div>
              ) : null}
            </DragOverlay>,
            document.body
          )
        : null}
    </DndContext>
  );
}

export default SortableFavoriteSearchGrid;
